import { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import DataTable from "@/components/DataTable";
import EntityForm from "@/components/EntityForm";
import ScadenzeDialog from "@/components/ScadenzeDialog";
import ImportAnagraficaDialog from "@/components/ImportAnagraficaDialog";
import { formatCell } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, CalendarClock, FileSpreadsheet, Upload } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 25;
const ENTITY_TYPE = { veicoli: "veicolo", autisti: "autista" };

export default function EntityPage({ entity }) {
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [filters, setFilters] = useState({});
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [scadRecord, setScadRecord] = useState(null);

  // reset when entity changes
  useEffect(() => {
    setMeta(null);
    setPage(1);
    setSortBy(null);
    setSortDir("asc");
    setSearch("");
    setDebounced("");
    setFilters({});
    api.get(`/meta/${entity}`).then((r) => setMeta(r.data)).catch(() => {});
  }, [entity]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const buildParams = useCallback(() => {
    const p = { page, page_size: PAGE_SIZE };
    if (sortBy) {
      p.sort_by = sortBy;
      p.sort_dir = sortDir;
    }
    if (debounced) p.search = debounced;
    Object.entries(filters).forEach(([k, v]) => {
      if (v) p[k] = v;
    });
    return p;
  }, [page, sortBy, sortDir, debounced, filters]);

  const load = useCallback(() => {
    if (!meta) return;
    setLoading(true);
    api
      .get(`/${entity}`, { params: buildParams() })
      .then((r) => {
        setRows(r.data.items);
        setTotal(r.data.total);
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [entity, meta, buildParams]);

  useEffect(() => {
    load();
  }, [load]);

  const listFields = useMemo(
    () => (meta ? meta.fields.filter((f) => f.list) : []),
    [meta]
  );

  const columns = useMemo(() => {
    return listFields.map((f) => ({
      key: f.name,
      label: f.label,
      align: f.type === "number" || f.type === "decimal" ? "right" : "left",
      render: (row) =>
        f.type === "bool" ? (
          <Badge
            variant="outline"
            className={
              row[f.name]
                ? "bg-green-50 text-green-700 border-green-200"
                : "bg-slate-100 text-slate-500 border-slate-200"
            }
          >
            {row[f.name] ? "Sì" : "No"}
          </Badge>
        ) : (
          formatCell(row[f.name], f)
        ),
    }));
  }, [listFields]);

  const filterFields = useMemo(() => {
    if (!meta) return [];
    return meta.fields
      .filter((f) => f.list && (f.type === "select" || f.type === "bool"))
      .map((f) => ({
        name: f.name,
        label: f.label,
        options:
          f.type === "bool"
            ? [
                { value: "true", label: "Sì" },
                { value: "false", label: "No" },
              ]
            : f.options.map((o) => ({ value: o, label: o })),
      }));
  }, [meta]);

  const onSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  const doExport = async () => {
    try {
      const params = buildParams();
      delete params.page;
      delete params.page_size;
      const res = await api.get(`/${entity}/export`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${entity}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/${entity}/${toDelete.id}`);
      toast.success("Eliminato");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const hasScadenze = ENTITY_TYPE[entity];

  const downloadModello = async () => {
    try {
      const res = await api.get(`/${entity}/modello`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `modello_${entity}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <ImportAnagraficaDialog open={importOpen} onOpenChange={setImportOpen} entity={entity} label={meta?.label || entity} onImported={load} />
      <DataTable
        testid={`table-${entity}`}
        columns={columns}
        rows={rows}
        loading={loading || !meta}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        onPage={setPage}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={onSort}
        search={search}
        onSearch={setSearch}
        filterFields={filterFields}
        filterValues={filters}
        onFilter={(name, val) => {
          setFilters((prev) => ({ ...prev, [name]: val }));
          setPage(1);
        }}
        onExport={doExport}
        leftToolbar={meta?.can_write ? (
          <>
            <Button variant="outline" size="sm" className="h-9" onClick={downloadModello} data-testid={`${entity}-modello`} title="Scarica modello Excel per l'importazione">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" />Modello
            </Button>
            <Button variant="outline" size="sm" className="h-9" onClick={() => setImportOpen(true)} data-testid={`${entity}-importa`}>
              <Upload className="h-4 w-4 mr-1.5" />Importa
            </Button>
          </>
        ) : null}
        onNew={() => {
          setEditing(null);
          setFormOpen(true);
        }}
        newLabel={meta?.singular ? `Nuovo ${meta.singular}` : "Nuovo"}
        canWrite={meta?.can_write}
        renderActions={(row) => (
          <div className="flex items-center justify-end gap-1">
            {hasScadenze && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-slate-500 hover:text-sky-700"
                onClick={() => setScadRecord(row)}
                data-testid={`row-scadenze-${row.id}`}
                title="Scadenze"
              >
                <CalendarClock className="h-4 w-4" />
              </Button>
            )}
            {meta?.can_write && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-slate-500 hover:text-sky-700"
                  onClick={() => {
                    setEditing(row);
                    setFormOpen(true);
                  }}
                  data-testid={`row-edit-${row.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-slate-500 hover:text-red-600"
                  onClick={() => setToDelete(row)}
                  data-testid={`row-delete-${row.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        )}
      />

      {meta && (
        <EntityForm
          open={formOpen}
          onOpenChange={setFormOpen}
          meta={meta}
          initial={editing}
          onSaved={load}
        />
      )}

      {hasScadenze && scadRecord && (
        <ScadenzeDialog
          open={!!scadRecord}
          onOpenChange={(v) => !v && setScadRecord(null)}
          entityType={ENTITY_TYPE[entity]}
          record={scadRecord}
        />
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi l'eliminazione?</AlertDialogTitle>
            <AlertDialogDescription>
              Il record verrà archiviato (eliminazione logica) e non sarà più visibile
              negli elenchi. L'operazione viene registrata nel log attività.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm-btn"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
